import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBdi} from '@github-ui/safe-html/VerifiedHTML'
import {PencilIcon} from '@primer/octicons-react'
import {IconButton, Link, PageHeader} from '@primer/react'
import {clsx} from 'clsx'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'
import type {HeaderViewer$key} from './__generated__/HeaderViewer.graphql'
import {HeaderMenu, type HeaderMenuBaseProps} from './HeaderMenu'
import styles from './HeaderViewer.module.css'
import {ViewPositionIndicator} from './ViewPositionIndicator'

export type HeaderViewerProps = {
  headerViewerKey: HeaderViewer$key
  parentKey?: HeaderParentTitle$key
  optionConfig: OptionConfig
} & HeaderMenuBaseProps

export function HeaderViewer({headerViewerKey, optionConfig, ...rest}: HeaderViewerProps) {
  const showInlineIssueNumber = !isFeatureEnabled('issues_sticky_header')
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment HeaderViewer on Issue {
        titleHTML
        number
        url
        viewerCanUpdateNext
        repository {
          name
          owner {
            login
          }
        }
        ...HeaderMenu
      }
    `,
    headerViewerKey,
  )
  const {titleHTML, number, url, repository, viewerCanUpdateNext} = data
  const canEditTitle = viewerCanUpdateNext && !optionConfig.isReadonly

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('issue', {
    owner: repository.owner.login,
    repo: repository.name,
    issueNumber: number,
  })

  const headerMenu = <HeaderMenu optionConfig={optionConfig} headerMenuData={data} {...rest} />

  // Props here correspond to only having 'Copy link' button rendered in HeaderMenu
  const areHeaderActionsMinimal =
    !optionConfig.showIssueCreateButton && !optionConfig.additionalHeaderActions && !optionConfig.customEditMenuEntries

  const hasNavState = !!optionConfig.viewNavState
  // When the sticky header is enabled, the ViewPositionIndicator renders inside
  // the sticky top container in IssueViewer instead of here, to keep a single
  // navigation surface across the page.
  const showNavState = hasNavState && !isFeatureEnabled('issues_sticky_header')

  return (
    <div aria-label="Header" role="region" data-testid={TEST_IDS.issueHeader} className={styles.headerRegion}>
      {showNavState && (
        <div className={styles.topBar}>
          <ViewPositionIndicator
            navigate={optionConfig.navigate}
            viewNavState={optionConfig.viewNavState}
            onLoadMore={optionConfig.onLoadMoreNavItems}
            onLoadBack={optionConfig.onLoadBackNavItems}
            pendingFocusDirection={optionConfig.viewNavState?.pendingFocusDirection}
            pendingNavSource={optionConfig.viewNavState?.pendingNavSource}
          />
          <div className={styles.topBarActions}>{headerMenu}</div>
        </div>
      )}
      <PageHeader
        className={clsx(
          styles.headerContainer,
          areHeaderActionsMinimal && styles.minimalHeader,
          isFeatureEnabled('issues_sticky_header') && styles.headerContainer_stickyHeader,
        )}
      >
        <PageHeader.TitleArea className={styles.PageHeader_TitleArea}>
          <PageHeader.Title
            as={'h1'}
            className={clsx(
              styles.PageHeader_Title,
              optionConfig.useViewportQueries ? styles.PageHeader_Title_viewport : styles.PageHeader_Title_container,
              !optionConfig.insideSidePanel && styles.PageHeader_Title_marginRight,
            )}
          >
            <VerifiedHTMLBdi
              className={clsx(
                'markdown-title',
                styles.issueTitle,
                optionConfig.insideSidePanel && styles.issueTitle_marginRight,
              )}
              /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
              html={titleHTML as SafeHTMLString}
              data-testid={TEST_IDS.issueTitle}
            />
            <span className="d-inline-flex flex-items-center gap-1">
              {optionConfig.insideSidePanel ? (
                <div className={styles.issueNumberContainer}>
                  <Link href={url} inline {...hovercardAttrs} className={styles.issueNumberLink}>
                    {LABELS.issueNumber(number)}
                  </Link>
                </div>
              ) : (
                showInlineIssueNumber && (
                  <span className={styles.issueNumberText}>
                    &nbsp;
                    {LABELS.issueNumber(number)}
                  </span>
                )
              )}
              {canEditTitle && (
                <IconButton
                  icon={PencilIcon}
                  onClick={() => rest.setIsIssueTitleEditActive?.(true)}
                  size="medium"
                  aria-label={LABELS.editTitle}
                  description={LABELS.editTitle}
                  variant="invisible"
                  data-testid={TEST_IDS.editIssueTitleButton}
                />
              )}
            </span>
          </PageHeader.Title>
        </PageHeader.TitleArea>
        {/* Mobile page viewport actions */}
        {!hasNavState && (
          <PageHeader.ContextArea>
            <PageHeader.ContextAreaActions className={styles.PageHeader_ContextAreaActions}>
              {headerMenu}
            </PageHeader.ContextAreaActions>
          </PageHeader.ContextArea>
        )}
        {/* Desktop page viewport, narrow container viewport actions */}
        {!hasNavState && !optionConfig.useViewportQueries && (
          // Only in the case where container-aware breakpoints are used
          <PageHeader.ContextArea hidden={false} className={styles.PageHeader_ContextArea}>
            <div className={styles.desktopMetadataContainer}>
              <div className={styles.metadataWrapper}>{headerMenu}</div>
            </div>
          </PageHeader.ContextArea>
        )}
        {!hasNavState && (
          <PageHeader.Actions
            className={clsx(
              styles.PageHeader_Actions,
              optionConfig.useViewportQueries
                ? styles.PageHeader_Actions_viewport
                : styles.PageHeader_Actions_container,
            )}
          >
            {headerMenu}
          </PageHeader.Actions>
        )}
      </PageHeader>
    </div>
  )
}
